// Lightweight shared read cache for the portal API layer — avoids re-fetching the
// same data (e.g. /sessions, /org/users) every time a page/tab mounts, and
// deduplicates concurrent in-flight requests for the same key (e.g. the
// Dashboard's KeepAlive'd tabs + the currently-routed tab both requesting
// /sessions on load).
//
// Deliberately not a full React Query/SWR adoption — this is a minimal,
// framework-agnostic cache at the fetch layer so existing page components
// don't need to change their state management, only their read calls.

interface CacheEntry<T> {
  expiresAt: number;
  value?: T;
  inFlight?: Promise<T>;
}

const DEFAULT_TTL_MS = 30_000;
const cache = new Map<string, CacheEntry<unknown>>();

export function cachedFetch<T>(key: string, fetcher: () => Promise<T>, ttlMs = DEFAULT_TTL_MS): Promise<T> {
  const now = Date.now();
  const entry = cache.get(key) as CacheEntry<T> | undefined;

  if (entry) {
    if (entry.inFlight) return entry.inFlight;
    if (entry.expiresAt > now && entry.value !== undefined) return Promise.resolve(entry.value);
  }

  const promise = fetcher()
    .then(value => {
      cache.set(key, { expiresAt: Date.now() + ttlMs, value });
      return value;
    })
    .catch(err => {
      cache.delete(key);
      throw err;
    });

  cache.set(key, { expiresAt: 0, inFlight: promise });
  return promise;
}

// Drops cached entries whose key starts with `prefix` — call after a mutation
// so the next read picks up fresh data instead of the stale cached value.
export function invalidateCache(prefix: string): void {
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

// Wipes every cached entry — call on sign-out / identity change so a cached
// response can never survive into a different signed-in user's session. Cache
// keys are also scoped by identity (see portalApi.ts) as a second layer of
// defense; this covers the moment of transition itself.
export function clearCache(): void {
  cache.clear();
}
